// ════════════════════════════════════════════════════════════════════════
//  Veröffentlichen
//
//  Reihenfolge mit Absicht: erst das Bundle in den Bucket, dann die Fassung als
//  veröffentlicht markieren.
//
//  Scheitert der Upload, ist nichts passiert. Scheitert das Markieren nach einem
//  erfolgreichen Upload, liegt eine Datei im Bucket, auf die niemand zeigt –
//  harmlos. Umgekehrt wäre es schlimm: eine veröffentlichte Fassung, deren
//  Bundle fehlt, und Klassen könnten daran gehängt werden.
// ════════════════════════════════════════════════════════════════════════

import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Bundle } from "@otto/lehrgang";
import type { Json, OttoClient } from "@otto/plattform";
import { VERSIONS_KEY } from "./data.js";
import { CURRICULA_KEY } from "../../curricula/lib/data.js";

const BUCKET = "inhalte";

function bundlePath(slug: string, version: number): string {
  return `${slug}/v${version}.json`;
}

export function usePublish(client: OttoClient) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      version: number;
      bundle: Bundle;
      notes: string;
      userId: string;
    }) => {
      const path = bundlePath(input.bundle.slug, input.version);
      const content = new Blob([JSON.stringify(input.bundle, null, 2)], {
        type: "application/json",
      });

      const { error: uploadError } = await client.storage
        .from(BUCKET)
        .upload(path, content, { upsert: true, contentType: "application/json" });
      if (uploadError) {
        throw new Error(`Bundle konnte nicht abgelegt werden: ${uploadError.message}`);
      }

      const { error } = await client
        .from("curriculum_versions")
        .update({
          content: input.bundle as unknown as Json,
          notes: input.notes,
          published_at: new Date().toISOString(),
          published_by: input.userId,
        })
        .eq("id", input.id);
      if (error) throw error;

      return { path };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: VERSIONS_KEY });
      // Auch die Lehrgangsliste: sie führt je Zeile den Stand, und der ist nach
      // dem Veröffentlichen ein anderer.
      void queryClient.invalidateQueries({ queryKey: CURRICULA_KEY });
    },
  });
}

/** Öffentliche Adresse eines veröffentlichten Bundles – für die Lern-App. */
export function bundleUrl(client: OttoClient, slug: string, version: number): string {
  return client.storage.from(BUCKET).getPublicUrl(bundlePath(slug, version)).data
    .publicUrl;
}
